
import { fetchZammadEmailLookup } from '@/helpers/fetchers';
import Icon from '@/components/UI/Icon.vue';
import { validateEmail, uuidv4 } from '@/utils/Common';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { useDebounceFn } from '@vueuse/core';

@Component({
  components: {
    Icon,
  },
})
export default class LookupMailbox extends Vue {
  @Prop({ type: String, required: true, default: 'To' }) public label: string;
  @Prop({ type: Boolean, required: false, default: false }) public hasError: boolean;
  @Prop({ type: Array, required: false }) public chipsProp: string[];

  private signalState: { [x: number]: AbortController }[] = [];

  public loading = false;

  // DEBOUNCE
  private debouncedRequest = useDebounceFn(() => 'response', 600);

  public clickedOutside(): void {
    this.isInputActive = false;
    this.isChipSectionActive = false;
    this.arrowIdx = -1;
    this.activeChipIdx = -1;
  }

  //
  // CHIPS SECTION
  //

  public chips: string[] = [];
  public activeChipIdx = -1;
  public selectItems: string[] = [];
  public arrowIdx = -1;
  public isChipSectionActive = false;
  public isInputActive = false;

  public inputValue = '';

  // Chips:
  @Watch('chipsProp', { immediate: true })
  private watchToChips(): void {
    if (this.chipsProp) {
      this.chips = this.chipsProp;
    }
  }

  //
  public chipFocused(idx) {
    this.activeChipIdx = idx;
  }

  //
  public deleteChip(index) {
    if (this.activeChipIdx > 0) {
      this.activeChipIdx--;
      this.setFocusOnChip(this.activeChipIdx);
    }
    this.chips.splice(index, 1);
    if (this.chips.length === 0) {
      this.setFocusOnInput();
    }
  }

  private setFocusOnChip(idx) {
    const chip = this.$refs[`chip${idx}`] as HTMLElement;
    if (chip) {
      chip[0].focus();
    }
  }

  public handleChipsKeyDown(event) {
    const key = event.key;
    const maxIndex = this.chips.length - 1;
    switch (key) {
      case 'ArrowLeft':
        if (this.activeChipIdx > 0) {
          this.activeChipIdx--;
          this.setFocusOnChip(this.activeChipIdx);
        }
        break;
      case 'ArrowRight':
        if (this.activeChipIdx < maxIndex) {
          this.activeChipIdx++;
          this.setFocusOnChip(this.activeChipIdx);
        } else {
          this.activeChipIdx = -1;
          this.setFocusOnInput();
        }
        break;
      case 'Backspace':
        this.deleteChip(this.activeChipIdx);
        break;
      case 'Escape':
        this.activeChipIdx = -1;
        break;
      case 'Tab':
        this.activeChipIdx = -1;
        break;
    }
  }

  // Input:
  public inputFocused() {
    this.isInputActive = true;
    this.isChipSectionActive = false;
    this.activeChipIdx = -1;
  }

  private setFocusOnInput() {
    const mailboxInput = this.$refs.mailboxInput as HTMLFormElement;
    if (mailboxInput) {
      mailboxInput.focus();
      this.isInputActive = true;
    }
  }

  public handleInputKeyDown(event) {
    //
    if (!this.isInputActive) {
      this.isInputActive = true;
      this.arrowIdx = -1;
      this.activeChipIdx = -1;
    }
    //
    switch (event.code) {
      case 'ArrowLeft':
        if (this.inputValue.length <= 0) {
          this.activeChipIdx = this.chips.length - 1;
          this.setFocusOnChip(this.activeChipIdx);
        }
        break;
      case 'Backspace':
        if (this.inputValue.length <= 0) {
          this.activeChipIdx = this.chips.length - 1;
          this.setFocusOnChip(this.activeChipIdx);
        }
        break;
      case 'ArrowDown':
        if (this.arrowIdx < this.selectItems.length - 1) {
          this.arrowIdx += 1;
          this.fixScrolling();
        }
        break;
      case 'ArrowUp':
        if (this.arrowIdx >= 1) {
          this.arrowIdx -= 1;
          this.fixScrolling();
        }
        break;
      case 'Enter':
        if (this.arrowIdx >= 0) {
          event.preventDefault();
          const item = this.selectItems[this.arrowIdx];
          this.chips.push(item);
          this.$emit('setChips', this.label, this.chips);
          this.inputValue = '';
          this.arrowIdx = -1;
          this.isInputActive = false;
        } else {
          this.saveChip();
        }
        break;
      case 'Tab':
        this.arrowIdx = -1;
        this.isInputActive = false;
        // if ()
        this.saveChip();
        break;
      case 'Escape':
        this.isInputActive = false;
        this.arrowIdx = -1;
        break;
    }
    //
  }

  public saveChip() {
    const { chips, inputValue } = this;
    if (inputValue.length >= 1 && validateEmail(inputValue)) {
      chips.push(inputValue);
      this.$emit('setChips', this.label, this.chips);
      this.inputValue = '';
    }
  }

  public handleItemClick(item: string): void {
    this.chips.push(item);
    this.$emit('setChips', this.label, this.chips);
    this.inputValue = '';
    this.setFocusOnInput();
  }

  public async inputChanged(): Promise<void> {
    if (this.inputValue.length >= 3) {
      const debounce = await this.debouncedRequest();
      if (debounce) {
        // TODO: Confirm no await needed
        this.getMailboxes();
      }
    }
  }

  private fixScrolling() {
    let scrollVal = 0;
    if (this.$refs.options) {
      const options = this.$refs.options as HTMLFormElement;
      // Find current option item
      options.map((option, idx) => {
        if (idx < this.arrowIdx) {
          scrollVal += option.clientHeight;
        }
      });
      // Scroll to top
      const scrollContainer = this.$refs.scrollContainer as HTMLFormElement;
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollVal;
      }
    }
  }

  // Get emails/mailboxes
  private getMailboxes() {
    this.loading = true;
    const inputValue = this.inputValue;
    const controller = new AbortController();
    const idx = uuidv4();
    this.signalState.push({
      [idx]: controller,
    });
    if (this.inputValue.length === 0) {
      return;
    }
    return fetchZammadEmailLookup(inputValue, controller.signal)
      .then(data => {
        // Ignore results if inputs has changed
        if (inputValue === this.inputValue) {
          // Cancel requests
          this.signalState.map(signal => Object.values(signal)[0].abort());
          this.signalState = [];
          // Apply data
          this.selectItems = data;
        }
      })
      .catch(() => null)
      .finally(() => {
        this.loading = false;
      });
  }
}
